import { selectItemInCart } from '@/app/modules/cart/state/common/entities/cart-item/cart-item.selectors';
import { selectEntireState } from '@/app/state/selectors';
import { createSelector } from '@ngrx/store';
import { State } from '@state/model';
import { PPTCart } from '../../../services/ppt-cart.service';
import { selectPPTAllowTaxReliefPayments, selectPPTAllowTaxFreezePayments } from '../../ppt-state/ppt-state.selectors';
import { selectPPTAccountBillsForCartOperations } from '../../ppt.selectors';
import { selectPPTBill, selectPPTBillOrDependenciesDelegated } from '../bill/ppt-bill.selectors';

const selectPPTAccountEntity = (s: State) => s.common.ppt.entities.accounts;

export const selectPPTAccounts = createSelector(selectPPTAccountEntity, s => s.ids.map(id => s.entities[id]!));

export const selectPPTAccountsByNumber = createSelector(selectPPTAccountEntity, s => s.entities);
export const selectPPTAccount = (accountNumber: string | undefined) => createSelector(
    selectPPTAccountsByNumber,
    accountsByNumber => accountNumber !== undefined ? accountsByNumber[accountNumber] : undefined
);

export const selectPPTAccountPath = (accountNumber: string | undefined) => createSelector(
    selectPPTAccountsByNumber,
    accountsByNumber => accountNumber !== undefined ? `/property-tax/bill/${accountNumber}` : undefined
);

export const selectPPTAccountHistoryLoaded = (accountNumber: string) => createSelector(
    selectPPTAccountsByNumber,
    accountsByNumber => !!accountsByNumber[accountNumber]?.bills
);

export const selectPPTAccountHistory = (accountNumber: string) => createSelector(
    selectPPTAccountsByNumber,
    accountsByNumber => accountsByNumber[accountNumber]?.bills
);

export const selectPPTAccountByBillId = (id: string) => createSelector(
    selectPPTAccounts,
    accounts =>
        accounts.find(account => account.bills?.includes(id) || account.payableBillsWithABalance?.includes(id))
);

export const selectPPTAccountHasPayableBills = (accountNumber: string, excludeDelegated = true) => createSelector(
    selectEntireState,
    s =>
    {
        const billsForCartOperations = selectPPTAccountBillsForCartOperations(accountNumber, excludeDelegated)(s);
        if (billsForCartOperations === undefined) return undefined;
        
        // Filter out tax relief and tax freeze blocked bills
        const allowTaxReliefPayments = selectPPTAllowTaxReliefPayments(s);
        const allowTaxFreezePayments = selectPPTAllowTaxFreezePayments(s);
        
        const payableBills = billsForCartOperations.filter(billId => 
{
            const bill = selectPPTBill(billId)(s);
            if (!bill) return false;
            
            const stateRelief = (bill as any).taxInformation?.stateRelief ?? 0;
            const countyRelief = (bill as any).taxInformation?.countyRelief ?? 0;
            const hasTaxRelief = stateRelief + countyRelief > 0;
            
            const stateFreeze = (bill as any).taxInformation?.stateFreeze ?? 0;
            const countyFreeze = (bill as any).taxInformation?.countyFreeze ?? 0;
            const hasTaxFreeze = stateFreeze + countyFreeze > 0;
            
            return (!hasTaxRelief || allowTaxReliefPayments) && (!hasTaxFreeze || allowTaxFreezePayments);
        });
        
        return payableBills ? !!payableBills.length : false;
    }
);

export const selectPPTAccountIsInCart = (accountNumber: string, excludeDelegated = true) => createSelector(
    selectEntireState,
    s =>
    {
        const paymentEnabledBills = selectPPTAccountBillsForCartOperations(accountNumber, excludeDelegated)(s);
        if (!paymentEnabledBills) return undefined;
        
        // Filter out tax relief and tax freeze blocked bills
        const allowTaxReliefPayments = selectPPTAllowTaxReliefPayments(s);
        const allowTaxFreezePayments = selectPPTAllowTaxFreezePayments(s);
        
        const billsToCheck = paymentEnabledBills.filter(billId => 
{
            const bill = selectPPTBill(billId)(s);
            if (!bill) return false;
            
            const stateRelief = (bill as any).taxInformation?.stateRelief ?? 0;
            const countyRelief = (bill as any).taxInformation?.countyRelief ?? 0;
            const hasTaxRelief = stateRelief + countyRelief > 0;
            
            const stateFreeze = (bill as any).taxInformation?.stateFreeze ?? 0;
            const countyFreeze = (bill as any).taxInformation?.countyFreeze ?? 0;
            const hasTaxFreeze = stateFreeze + countyFreeze > 0;
            
            return (!hasTaxRelief || allowTaxReliefPayments) && (!hasTaxFreeze || allowTaxFreezePayments);
        });

        return billsToCheck.length
            ? billsToCheck
                .map(billId => selectItemInCart(PPTCart.BILL_CART_TYPE, billId)(s))
                .reduce(
                    (result, billInCart) => !!billInCart && result,
                    true
                )
            : false;
    }
);

export const selectPPTAccountHasPayableBillsDelegated = (accountNumber: string) => createSelector(
    selectEntireState,
    s =>
    {
        const billIds = selectPPTAccountBillsForCartOperations(accountNumber, false)(s);
        if (billIds === undefined) return undefined;

        const billsDelegated = billIds.map(billId => selectPPTBillOrDependenciesDelegated(billId)(s));
        if (billsDelegated.includes(true)) return true;

        return false;
    }
);

export const selectPPTAccountHasALLPayableBillsDelegated = (accountNumber: string) => createSelector(
    selectEntireState,
    s =>
    {
        const billIds = selectPPTAccountBillsForCartOperations(accountNumber, false)(s);
        if (billIds === undefined) return undefined;

        const billsDelegated = billIds.map(billId => selectPPTBillOrDependenciesDelegated(billId)(s));
        if (billsDelegated.includes(false)) return false;

        return true;
    }
);

export const selectPPTAccountAddAllButtonTitle = (accountNumber: string) => createSelector(
    selectEntireState,
    s =>
    {
        const account = selectPPTAccount(accountNumber)(s);
        if (!account) return '';
        
        // For lookup page, use payableBillsWithABalance
        if (account.payableBillsWithABalance && account.payableBillsWithABalance.length > 0) 
{
            const inCart = selectPPTAccountIsInCart(accountNumber)(s);
            return inCart ? 'Remove bills for this parcel from the cart.' : 'Add bills for this parcel to the cart.';
        }
        
        // For bill page, use detailed logic
        const hasPayableBills = selectPPTAccountHasPayableBills(accountNumber)(s);
        return hasPayableBills === undefined
            ? ''
            : hasPayableBills
                ? !selectPPTAccountIsInCart(accountNumber)(s)
                    ? 'Add bills for this parcel to the cart.'
                    : 'Remove bills for this parcel from the cart.'
                : 'This account has no bills available for payment.';
    }
);

export const selectPPTAccountAddAllButtonEnabled = (accountNumber: string) => createSelector(
    selectEntireState,
    s =>
{
        const account = selectPPTAccount(accountNumber)(s);
        if (!account) return false;

        // If account has payableBillsWithABalance, use that for lookup page
        if (account.payableBillsWithABalance && account.payableBillsWithABalance.length > 0)
{
            return true;
        }

        // Otherwise check detailed bill data (for bill page)
        const billsForCartOperations = selectPPTAccountBillsForCartOperations(accountNumber, false)(s);
        if (!billsForCartOperations) return false;

        return billsForCartOperations.some(billId =>
{
            const bill = selectPPTBill(billId)(s);
            return bill && bill.due > 0 && !bill.paymentBlocked;
        });
    }
);

export const selectPPTAccountAddAllShowWarningIcon = (accountNumber: string) => createSelector(
    selectEntireState,
    s => selectPPTAccountHasPayableBillsDelegated(accountNumber)(s)
);
